import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import apiManagerSystem from "api/manager-systems";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Loader } from "components/common/Loader";
import DialogDisplay from "components/Dialog/DialogDisplay";
import { DialogManagerProvider, resetManagedDialogs } from "components/DialogV3/DialogManagerContext";
import { decode } from "js-base64";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import router from "routes/root";
import { AuthFlowProvider, resetAuthFlow } from 'pages/authentication/AuthFlowContext';
import { setSystemConfigs } from "store/commonSlice";
import { purgeUserPersistedStorage } from 'store/persistence';
import { selectSessionStatus } from 'store/selectors';
import {
  SESSION_STATUS,
  endSession,
  sessionAuthenticated,
  sessionBootstrapped,
  sessionEnded,
} from 'store/sessionSlice';
import { updatePathApp } from "store/user/userSlice";
import { getAccessToken, getRefreshToken, SESSION_COOKIE_EVENT } from "utils/cookies";
import { setTimeoutConfirmRefreshToken } from "utils/helpers";
import { clearConfirmRefreshTimer } from 'services/sessionTimerService';
import apiManagerUser from "./api/manager-users";
import "./App.css";
import { URL_LANDING_PAGE } from "./constants/api";
import './index.css';
import './pages/subtitles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
});

function App() {
  const dispatch = useDispatch()
  const sessionStatus = useSelector(selectSessionStatus)

  const getUserRoles = (accessToken) => {
    const isGuest = JSON.parse(decode(accessToken.split('.')[1]))?.context?.user?.role === 'tmp_guest'

    if (isGuest) {
      apiManagerUser.logout()
      window.localStorage.removeItem('checkEventPopupByUser')
      dispatch(endSession())
      window.location.href = URL_LANDING_PAGE
    }
    // get user roles
  }

  useEffect(() => {
    const accessToken = getAccessToken();
    dispatch(sessionBootstrapped(Boolean(accessToken)));
    if (accessToken) getUserRoles(accessToken);

    if (!window.location.pathname.includes("/auth/") && !window.location.pathname.includes("/reset-password") && !window.location.pathname.includes("iam/callback")) {
      dispatch(updatePathApp(window.location.pathname))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    const syncSessionFromCookies = (event) => {
      const authenticated = Boolean(event.detail?.authenticated);
      if (!authenticated) {
        resetAuthFlow();
        resetManagedDialogs();
        queryClient.clear();
        clearConfirmRefreshTimer();
        purgeUserPersistedStorage();
      }
      dispatch(authenticated ? sessionAuthenticated() : sessionEnded());
    };

    window.addEventListener(SESSION_COOKIE_EVENT, syncSessionFromCookies);
    return () => window.removeEventListener(SESSION_COOKIE_EVENT, syncSessionFromCookies);
  }, [dispatch]);

  useEffect(() => {
    const getSystemConfigs = async () => {
      if (sessionStatus !== SESSION_STATUS.AUTHENTICATED || !getAccessToken()) return

      try {
        const res = await apiManagerSystem.getConfigSystems()
        if (res.status === 200) {
          dispatch(setSystemConfigs(res.data))
          if (getRefreshToken()) {
            setTimeoutConfirmRefreshToken()
          }
        }
      } catch (error) {
        console.log('[Error] getSystemConfigs: ', error)
      }
    }

    getSystemConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStatus, dispatch])

  return (
    <React.Fragment>
      <QueryClientProvider client={queryClient}>
        <AuthFlowProvider>
          <DialogManagerProvider>
            <ToastContainer
              position="top-center"
              autoClose={2500}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
              limit={3}
            />
            <RouterProvider router={router} fallbackElement={<Loader />} />
            <DialogDisplay />
          </DialogManagerProvider>
        </AuthFlowProvider>
      </QueryClientProvider>
    </React.Fragment>
  );
}

export default App;
